import {
  ClipboardCopyIcon,
  ShareIcon,
  SparklesIcon,
} from '@heroicons/react/outline';
import * as Diff from 'diff';
import dynamic from 'next/dynamic';
import useLocale from 'next-translate/useTranslation';
import React, { useState } from 'react';
import { Completion } from 'types';

import clsxm from '@/lib/clsxm';
import { copyTextToClipboard } from '@/lib/copyTextToClipboard';
import {
  formatLotterySetsAsText,
  isLotteryPreset,
  lotterySpecialLabel,
  parseLotterySets,
} from '@/lib/lottery';

import { useAnalytics } from '@/contexts/Analytics';

import LotteryBalls from './presets/LotteryBalls';
import ShareOutputModal, { OutputSharing } from './ShareOutputModal';
import RichText from './templates/landing/RichText';
import { CaptionItem } from '../types/Caption';

// The chat renderer brings a syntax highlighter and KaTeX (over 1 MB), so it
// loads on demand; the template workspace renders through RichText instead.
const MarkdownBlock = dynamic(() => import('./Chat/MarkdownBlock'));

type CaptionProps = {
  caption: CaptionItem;
  completion?: Completion;
  // Opens the public pricing modal. Passed by PublicTemplateForm for freshly
  // generated outputs; absent on other surfaces (e.g. SSR examples), where the
  // upsell row is not rendered.
  onUpgradeClick?: () => void;
  // `panel`: borderless, inside the template workspace's output pane.
  variant?: 'card' | 'panel';
};

const handleNewLines = (text: string) => {
  const items = text.split('\n');
  return items.map((item, key) => {
    return (
      <React.Fragment key={key}>
        {item}
        {key !== items.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

// Copy / Share in the template workspace's output pane.
const panelActionClass =
  'inline-flex h-8 items-center gap-1.5 rounded-full bg-white px-3 text-[13px] font-medium text-[#3c3c52] ring-1 ring-inset ring-[#0f0f28]/10 transition hover:ring-[#0f0f28]/25 dark:bg-white/[0.04] dark:text-[#c4c4da] dark:ring-white/10 dark:hover:ring-white/25';

const PublicCompletion = ({
  caption,
  completion,
  onUpgradeClick,
  variant = 'card',
}: CaptionProps) => {
  const isPanel = variant === 'panel';
  const { t } = useLocale('common');
  const analytics = useAnalytics();
  const [isCopied, setIsCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharing, setSharing] = useState<OutputSharing>({});
  const isNewsArticle =
    (completion?.preset || caption.preset) === 'news-article-generator';

  // Lottery-style presets emit JSON describing number sets; parse it so we can
  // render visual "balls" and copy readable text (never raw JSON).
  const lotteryPresetSlug = completion?.preset || caption.preset;
  const lotterySets = isLotteryPreset(lotteryPresetSlug)
    ? parseLotterySets(caption.text)
    : null;
  const displayText = lotterySets
    ? formatLotterySetsAsText(
        lotterySets,
        lotterySpecialLabel(lotteryPresetSlug),
      )
    : caption.text;

  // Card presets prepend the generated card as image markdown. Copy should
  // yield just the message text, and the card gets a signup CTA (the public
  // card is watermarked — the clean version is the signup incentive).
  const hasCardImage = /^!\[[^\]]*\]\([^)]+\)/.test(caption.text);
  const copyText = hasCardImage
    ? displayText.replace(/^!\[[^\]]*\]\([^)]*\)\s*/, '')
    : displayText;
  const signupHref = `/login?preset=${encodeURIComponent(
    lotteryPresetSlug || '',
  )}#auth-sign-up`;

  const trackCopy = (method: 'button' | 'selection') => {
    analytics.track('Copied text', {
      preset: lotteryPresetSlug,
      output_id: caption.id,
      method,
      source: 'public',
    });
  };

  // onClick handler function for the copy button
  const handleCopyClick = () => {
    // Asynchronously call copyTextToClipboard
    copyTextToClipboard(copyText)
      .then(() => {
        trackCopy('button');
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 1500);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // Many users copy by highlighting text instead of clicking Copy. Capture
  // those native selection-copies too, attributed to the preset.
  const handleSelectionCopy = () => {
    const selected = window.getSelection()?.toString() ?? '';
    if (selected.trim().length < 2) return;
    trackCopy('selection');
  };

  const generateDiff = (source: string, fixed: string) => {
    if (!fixed) {
      return source;
    }

    const diff = Diff.diffWords(fixed, source);

    const fixedTextDiff = diff.map((part: any, i: number) => {
      if (part.added) {
        return (
          <span key={i} className='bg-green-100 dark:bg-green-900'>
            {handleNewLines(part.value)}
          </span>
        );
      }
      if (part.removed) {
        return (
          <span key={i} className='bg-red-100 dark:bg-red-900'>
            {handleNewLines(part.value)}
          </span>
        );
      }

      return <span key={i}>{handleNewLines(part.value)}</span>;
    });

    return <>{fixedTextDiff}</>;
  };

  const shouldShowDiff =
    completion?.preset === 'grammar-corrector' ||
    caption?.preset === 'grammar-corrector' ||
    completion?.preset === 'paraphrasing-tool' ||
    caption?.preset === 'paraphrasing-tool' ||
    completion?.preset === 'native-speaker' ||
    caption?.preset === 'native-speaker';

  return (
    <li
      key={caption.id}
      className={
        isPanel
          ? 'relative list-none'
          : 'messages relative bg-white px-4 py-6 shadow dark:bg-slate-800 sm:rounded-lg sm:p-6'
      }
    >
      <article aria-labelledby={'caption-title-' + caption.id}>
        <div
          className={
            isPanel
              ? 'text-[15px] text-[#3c3c52] dark:text-[#c4c4da]'
              : 'message text-md mt-2 space-y-4 text-gray-700 dark:text-slate-400'
          }
          onCopy={handleSelectionCopy}
        >
          {lotterySets ? (
            <LotteryBalls sets={lotterySets} preset={lotteryPresetSlug} />
          ) : shouldShowDiff ? (
            <>
              {generateDiff(
                caption.text,
                caption?.prompt || (completion?.user_prompt as string),
              )}
            </>
          ) : isPanel ? (
            <RichText text={caption.text} />
          ) : (
            <MarkdownBlock
              message={caption.text.replace(/\n/g, '  \n').trim()}
              preferLinkText={isNewsArticle}
            />
          )}
        </div>
        {hasCardImage && (
          <div className='mt-5 flex flex-wrap items-center gap-3'>
            <a
              href={signupHref}
              className={clsxm(
                'inline-flex items-center bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700',
                isPanel
                  ? 'rounded-full px-5 py-2.5 font-semibold'
                  : 'rounded-md',
              )}
            >
              {t('Download in HD — Sign up free')}
            </a>
            <span className='text-xs text-gray-400 dark:text-slate-500'>
              {t('Removes the watermark')}
            </span>
          </div>
        )}
        {isPanel ? (
          <div className='mt-6 border-t border-[#0f0f28]/[0.07] pt-4 dark:border-white/[0.08]'>
            <div className='flex flex-wrap items-center gap-2'>
              <button
                type='button'
                onClick={handleCopyClick}
                className={panelActionClass}
              >
                <ClipboardCopyIcon className='h-4 w-4' aria-hidden='true' />
                {isCopied ? t('Copied!') : t('Copy')}
              </button>
              {caption.uuid && (
                <button
                  type='button'
                  onClick={() => setShowShareModal(true)}
                  className={panelActionClass}
                >
                  <ShareIcon className='h-4 w-4' aria-hidden='true' />
                  {t('Share')}
                </button>
              )}
              {onUpgradeClick && !hasCardImage && (
                <button
                  type='button'
                  onClick={() => {
                    analytics.track('Public Regenerate Upgrade Click', {
                      preset: lotteryPresetSlug,
                      output_id: caption.id,
                      source: 'public',
                    });
                    onUpgradeClick();
                  }}
                  className='ms-auto inline-flex h-8 items-center gap-1.5 rounded-full bg-primary-50 px-3 text-[13px] font-semibold text-primary-700 ring-1 ring-inset ring-primary-200 transition-colors hover:bg-primary-100 dark:bg-primary-400/10 dark:text-primary-200 dark:ring-primary-400/30 dark:hover:bg-primary-400/20'
                >
                  <SparklesIcon className='h-4 w-4' aria-hidden='true' />
                  {t('Regenerate with Advanced AI')}
                </button>
              )}
            </div>
            {onUpgradeClick && !hasCardImage && (
              <p className='mt-2.5 text-xs text-[#6f6f86] dark:text-[#7b7b96]'>
                {t('Generated with the free Standard model.')}
              </p>
            )}
          </div>
        ) : (
          <div
            className={
              onUpgradeClick && !hasCardImage
                ? 'mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4 dark:border-slate-700'
                : 'mt-5 flex justify-end'
            }
          >
            {/* Post-output upsell: the moment after seeing a decent result is
                peak "could this be better?" — reframe the free output as the
                floor and route the curiosity to the pricing modal. Card outputs
                keep the HD-download CTA as their single primary action. */}
            {onUpgradeClick && !hasCardImage && (
              <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
                <span className='text-xs text-gray-400 dark:text-slate-500'>
                  {t('Generated with the free Standard model.')}
                </span>
                <button
                  type='button'
                  onClick={() => {
                    analytics.track('Public Regenerate Upgrade Click', {
                      preset: lotteryPresetSlug,
                      output_id: caption.id,
                      source: 'public',
                    });
                    onUpgradeClick();
                  }}
                  className='inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100 dark:border-primary-400/30 dark:bg-primary-400/10 dark:text-primary-300 dark:hover:bg-primary-400/20'
                >
                  <SparklesIcon className='h-4 w-4' aria-hidden='true' />
                  {t('Regenerate with Advanced AI')}
                </button>
              </div>
            )}
            <div className='flex items-center'>
              {caption.uuid && (
                <span className='mr-4 inline-flex items-center text-sm'>
                  <button
                    type='button'
                    className='inline-flex space-x-2 text-gray-400 hover:text-gray-500 dark:text-slate-500 hover:dark:text-slate-300'
                    onClick={() => setShowShareModal(true)}
                  >
                    <ShareIcon className='h-5 w-5' aria-hidden='true' />
                    <span className='font-medium text-gray-900 dark:text-slate-500 hover:dark:text-slate-300'>
                      {t('Share')}
                    </span>
                  </button>
                </span>
              )}
              <span className='inline-flex items-center text-sm'>
                <button
                  type='button'
                  className='inline-flex space-x-2 text-gray-400 hover:text-gray-500 dark:text-slate-500 hover:dark:text-slate-300'
                  onClick={handleCopyClick}
                >
                  <ClipboardCopyIcon className='h-5 w-5' aria-hidden='true' />
                  <span className='font-medium text-gray-900 dark:text-slate-500 hover:dark:text-slate-300'>
                    {isCopied ? t('Copied!') : t('Copy')}
                  </span>
                </button>
              </span>
            </div>
          </div>
        )}
      </article>

      {caption.uuid && (
        <ShareOutputModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          apiPath='/api/share-public-completion'
          payload={{ uuid: caption.uuid }}
          sharing={sharing}
          onChange={setSharing}
        />
      )}
    </li>
  );
};

export default PublicCompletion;
